import React from 'react';
import PropTypes from 'prop-types';
import { brandColors, interactive } from '../../tokens/colors';

/**
 * IconButton Component
 * 
 * A compact button for icon-only actions.
 * Supports circular and square shapes, multiple variants, and sizes.
 * 
 * @example
 * // Basic icon button (circular)
 * <IconButton
 *   icon={<SearchIcon />}
 *   onClick={handleSearch}
 *   aria-label="Search"
 * />
 * 
 * @example
 * // Primary variant with tooltip
 * <IconButton
 *   icon={<UserIcon />}
 *   variant="primary"
 *   onClick={openProfile}
 *   title="User profile"
 *   aria-label="Open user profile"
 * />
 * 
 * @example
 * // Square shape for toolbar
 * <IconButton
 *   icon={<EditIcon />}
 *   shape="square"
 *   variant="ghost"
 *   size="sm"
 * />
 */
const IconButton = ({
  icon,
  onClick,
  type = 'button',
  variant = 'neutral',
  size = 'md',
  shape = 'circular',
  disabled = false,
  className = '',
  ...rest
}) => {
  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  // Shape classes
  const shapeClasses = {
    circular: 'rounded-full',
    square: 'rounded-md',
  };

  // Variant-specific classes (theme-aware)
  const variantClasses = {
    neutral: [
      'bg-gray-100 dark:bg-dark-800',
      'hover:bg-gray-200 dark:hover:bg-dark-700',
      'text-gray-700 dark:text-dark-300',
      'hover:text-gray-900 dark:hover:text-dark-100',
    ],
    primary: [
      brandColors.primaryBg,
      brandColors.primaryBgHover,
      'text-white',
    ],
    secondary: [
      'bg-white dark:bg-dark-900',
      'hover:bg-gray-100 dark:hover:bg-dark-800',
      'text-gray-700 dark:text-dark-300',
      'border',
      'border-gray-300 dark:border-dark-600',
    ],
    ghost: [
      'bg-transparent',
      'hover:bg-gray-100 dark:hover:bg-dark-800',
      'text-gray-600 dark:text-dark-400',
      'hover:text-gray-900 dark:hover:text-dark-100',
    ],
    danger: [
      'bg-red-50 dark:bg-red-900/20',
      'hover:bg-red-100 dark:hover:bg-red-900/30',
      'text-red-600 dark:text-red-400',
      'hover:text-red-700 dark:hover:text-red-300',
    ],
    success: [
      'bg-green-50 dark:bg-green-900/20',
      'hover:bg-green-100 dark:hover:bg-green-900/30',
      'text-green-600 dark:text-green-400',
      'hover:text-green-700 dark:hover:text-green-300',
    ],
    glass: [
      interactive.glassIconBtn,
    ],
  };

  const classes = [
    'inline-flex',
    'items-center',
    'justify-center',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    brandColors.focus,
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    sizeClasses[size] || sizeClasses.md,
    shapeClasses[shape] || shapeClasses.circular,
    ...(variantClasses[variant] || variantClasses.neutral),
    className,
  ].join(' ');

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      {...rest}
    >
      {icon}
    </button>
  );
};

IconButton.propTypes = {
  /** Icon to display (SVG or React component) */
  icon: PropTypes.node.isRequired,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Visual variant */
  variant: PropTypes.oneOf(['neutral', 'primary', 'secondary', 'ghost', 'danger', 'success', 'glass']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Button shape */
  shape: PropTypes.oneOf(['circular', 'square']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Accessible label (required for icon-only buttons) */
  'aria-label': PropTypes.string.isRequired,
};

export default IconButton;
